/**
 * <tv-pdp> owns variant + quantity state for the TotalVet PDP.
 * It updates: price, compare-at, gallery slide, bundle tier prices, buy button total,
 * sticky bar, the product form's hidden inputs and the URL. It then broadcasts
 * `tv:variant-change` on document so other sections (tv-panel-contents) can react.
 */
class TvPdp extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-tv-variants]');
    try {
      this.variants = JSON.parse(json ? json.textContent : '[]');
    } catch (e) {
      this.variants = [];
    }
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.qty = 1;
    this.pct = 0;

    const fromUrl = new URL(window.location.href).searchParams.get('variant');
    const initial = this.findVariant(fromUrl) || this.findVariant(this.dataset.selected) || this.variants[0];
    this.variant = initial;

    this.bindVariantCards();
    this.bindBundle();
    this.bindGallery();
    this.bindSticky();
    this.bindTabs();
    this.bindVcBadge();
    this.bindBuyNow();

    // Sync everything to the initial state (URL may differ from server-rendered default).
    if (initial) this.setVariant(initial.id, { silentUrl: !fromUrl, instant: true });

    this.trackProductView();
  }

  /* ---------- PostHog (product_viewed, add_to_cart, checkout_started) ----------
   * Captured here with posthog-js so they land on the visitor's own PostHog person.
   * Buy Now's checkout_started goes through window.tvPhCheckout (assets/posthog.js), which also
   * handles the cart's checkout buttons. checkout_completed comes from Shopify's Order creation
   * webhook (PostHog function "Shopify orders → checkout_completed"), which finds this visitor
   * through the ph_distinct_id cart attribute.
   */
  phProps(variant, qty) {
    if (!variant) return {};
    const price = (Number(variant.price) || 0) / 100;
    const props = {
      product_id: Number(this.dataset.phProductId) || undefined,
      product_title: this.dataset.phProductTitle || undefined,
      variant_id: variant.id,
      variant_title: variant.title,
      sku: variant.sku || undefined,
      price,
      currency: this.dataset.phCurrency || undefined,
    };
    if (qty) {
      props.quantity = qty;
      props.value = Math.round(price * qty * 100) / 100;
    }
    return props;
  }

  // posthog.js loads async: wait for the window.posthog stub so the call is queued, not dropped.
  phCapture(name, props, options) {
    let tries = 0;
    const go = () => {
      const ph = window.posthog;
      if (ph && typeof ph.capture === 'function') {
        try {
          ph.capture(name, props, options);
        } catch (e) {}
        return;
      }
      if (tries++ < 75) setTimeout(go, 200);
    };
    go();
  }

  trackProductView() {
    if (this.viewTracked || !this.variant) return;
    this.viewTracked = true;
    this.phCapture('product_viewed', this.phProps(this.variant));
  }

  // Runs after /cart/add.js succeeds and before the redirect to checkout. Sends add_to_cart
  // with sendBeacon so the navigation can't cancel it, writes ph_distinct_id to the cart (its
  // response is the updated cart), then sends checkout_started for that cart. Waits at most
  // ~3s in total; if PostHog never loads (ad blocker) it gives up and the redirect proceeds.
  async trackAddToCart(form) {
    try {
      const data = new FormData(form);
      const variant = this.findVariant(data.get('id')) || this.variant;
      const qty = Number(data.get('quantity')) || this.qty || 1;
      const helper = window.tvPhCart;
      const ready = helper ? await helper.whenReady(1500) : false;
      if (!ready) return;
      window.posthog.capture('add_to_cart', Object.assign(this.phProps(variant, qty), { add_source: 'buy_now' }), {
        transport: 'sendBeacon',
        send_instantly: true,
      });
      await Promise.race([helper.stamp(true), new Promise((resolve) => setTimeout(resolve, 1500))]);
      if (window.tvPhCheckout) window.tvPhCheckout.started('buy_now');
    } catch (e) {}
  }

  findVariant(id) {
    if (!id) return null;
    const num = Number(id);
    return this.variants.find((v) => v.id === num) || null;
  }

  /* ---------- Money ---------- */
  formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    const withSep = amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const noDec = Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return this.moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, withSep)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.toFixed(2).replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDec);
  }

  /* ---------- Variant cards ---------- */
  bindVariantCards() {
    this.querySelectorAll('[data-tv-radio]').forEach((radio) => {
      radio.addEventListener('change', () => {
        if (radio.checked) this.setVariant(radio.value);
      });
    });
  }

  setVariant(id, opts = {}) {
    const v = this.findVariant(id);
    if (!v) return;
    this.variant = v;

    // Cards
    this.querySelectorAll('[data-tv-card]').forEach((card) => {
      const on = Number(card.dataset.variantId) === v.id;
      card.classList.toggle('tv-card--selected', on);
      const radio = card.querySelector('[data-tv-radio]');
      if (radio) radio.checked = on;
    });

    // Form inputs
    this.querySelectorAll('[data-tv-variant-input]').forEach((input) => {
      input.value = v.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // Price + compare
    const price = this.querySelector('[data-tv-price]');
    if (price) price.textContent = this.formatMoney(v.price);
    const compare = this.querySelector('[data-tv-compare]');
    if (compare) {
      if (v.compare_at_price > v.price) {
        compare.textContent = this.formatMoney(v.compare_at_price);
        compare.hidden = false;
      } else {
        compare.hidden = true;
      }
    }

    // Buy button
    const btn = this.querySelector('[data-tv-buy-button]');
    if (btn) btn.disabled = !v.available;
    const stickyBtn = this.querySelector('[data-tv-sticky-button]');
    if (stickyBtn) stickyBtn.disabled = !v.available;

    // Gallery
    if (v.media_id) this.showMedia(v.media_id, opts.instant);

    // Sticky name
    const stickyName = this.querySelector('[data-tv-sticky-name]');
    if (stickyName) stickyName.textContent = v.name;

    this.updateTotals();

    // URL
    if (!opts.silentUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url.toString());
    }

    document.dispatchEvent(
      new CustomEvent('tv:variant-change', {
        bubbles: true,
        detail: { variant: v, sectionId: this.dataset.sectionId },
      })
    );
  }

  /* ---------- Bundle tiers ---------- */
  bindBundle() {
    this.querySelectorAll('[data-tv-tier]').forEach((pill) => {
      pill.addEventListener('click', () => {
        this.qty = Number(pill.dataset.qty) || 1;
        this.pct = Number(pill.dataset.pct) || 0;
        this.querySelectorAll('[data-tv-tier]').forEach((p) => {
          const on = p === pill;
          p.classList.toggle('is-selected', on);
          p.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        this.updateTotals();
      });
    });
  }

  updateTotals() {
    const v = this.variant;
    if (!v) return;

    // Per-tier "each" prices follow the selected variant.
    this.querySelectorAll('[data-tv-tier]').forEach((pill) => {
      const pct = Number(pill.dataset.pct) || 0;
      const qty = Number(pill.dataset.qty) || 1;
      const each = Math.round(v.price * (100 - pct) / 100);
      const el = pill.querySelector('[data-tv-each]');
      if (el) el.textContent = qty > 1 ? `${this.formatMoney(each)} each` : this.formatMoney(each);
    });

    const each = Math.round(v.price * (100 - this.pct) / 100);
    const total = each * this.qty;

    this.querySelectorAll('[data-tv-qty-input]').forEach((input) => (input.value = this.qty));

    const totalEl = this.querySelector('[data-tv-buy-total]');
    if (totalEl) totalEl.textContent = this.qty > 1 ? `${this.formatMoney(total)} for ${this.qty}` : this.formatMoney(total);

    const stickyPrice = this.querySelector('[data-tv-sticky-price]');
    if (stickyPrice) stickyPrice.textContent = this.qty > 1 ? `${this.qty} kits · ${this.formatMoney(total)}` : this.formatMoney(total);
  }

  /* ---------- Gallery ---------- */
  bindGallery() {
    this.track = this.querySelector('[data-tv-track]');
    this.slides = Array.from(this.querySelectorAll('[data-tv-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-tv-thumb]'));
    if (!this.track) return;

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.showMedia(thumb.dataset.mediaId));
    });

    // Keep thumbnails in sync with manual swipes.
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
              this.markThumb(entry.target.dataset.mediaId);
            }
          });
        },
        { root: this.track, threshold: [0.6] }
      );
      this.slides.forEach((s) => io.observe(s));
    }
  }

  showMedia(mediaId, instant) {
    if (!this.track) return;
    const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
    if (!slide) return;
    const left = slide.offsetLeft;
    if (instant) {
      this.track.style.scrollBehavior = 'auto';
      this.track.scrollLeft = left;
      this.track.style.scrollBehavior = '';
    } else {
      this.track.scrollTo({ left, behavior: 'smooth' });
    }
    this.markThumb(mediaId);
  }

  markThumb(mediaId) {
    const row = this.querySelector('[data-tv-thumbs]');
    this.thumbs.forEach((t) => {
      const on = t.dataset.mediaId === String(mediaId);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      // Scroll the thumbnail row horizontally only; never move the page.
      if (on && row && row.scrollWidth > row.clientWidth) {
        const left = t.offsetLeft - (row.clientWidth - t.clientWidth) / 2;
        row.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      }
    });
  }

  /* ---------- Info tabs ---------- */
  bindTabs() {
    this.querySelectorAll('[data-tv-tabs]').forEach((wrap) => {
      const tabs = Array.from(wrap.querySelectorAll('[data-tv-tab]'));
      const panels = Array.from(wrap.querySelectorAll('[data-tv-panel]'));
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => {
          tabs.forEach((t, j) => {
            const on = i === j;
            t.classList.toggle('is-active', on);
            t.setAttribute('aria-selected', on ? 'true' : 'false');
            if (panels[j]) {
              panels[j].hidden = !on;
              panels[j].classList.toggle('is-active', on);
            }
          });
        });
      });
    });
  }

  /* ---------- Veterinarians' Choice badge dialog ---------- */
  bindVcBadge() {
    const dialog = this.querySelector('[data-tv-vc-dialog]');
    const opener = this.querySelector('[data-tv-vc-open]');
    if (!dialog || !opener || typeof dialog.showModal !== 'function') return;
    const close = () => dialog.close();
    opener.addEventListener('click', () => {
      dialog.showModal();
      document.body.classList.add('tv-vc-open');
    });
    dialog.querySelectorAll('[data-tv-vc-close]').forEach((btn) => btn.addEventListener('click', close));
    dialog.addEventListener('click', (event) => {
      // Click on the backdrop (outside the dialog box) closes it.
      const r = dialog.getBoundingClientRect();
      const inside = event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
      if (!inside) close();
    });
    dialog.addEventListener('close', () => document.body.classList.remove('tv-vc-open'));
  }

  /* ---------- Buy now (straight to checkout) ---------- */
  bindBuyNow() {
    if (this.dataset.buyAction !== 'checkout') return;
    const form = this.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;
    // Horizon's product-form-component handler would also add the item and open the cart drawer.
    // It listens on document in the capture phase (before this listener), so the section leaves
    // out on:submit when buy_action is "checkout" (sections/tv-pdp-main.liquid).
    this.addEventListener(
      'submit',
      (event) => {
        if (event.target !== form) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.buyNow(form);
      },
      { capture: true }
    );
  }

  async buyNow(form) {
    if (this.buying) return;
    this.buying = true;
    const buttons = [this.querySelector('[data-tv-buy-button]'), this.querySelector('[data-tv-sticky-button]')].filter(Boolean);
    const errorEl = form.querySelector('[ref="addToCartTextError"]');
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    buttons.forEach((b) => {
      b.disabled = true;
      b.setAttribute('aria-busy', 'true');
    });
    if (errorEl) errorEl.classList.add('hidden');
    try {
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.description || data.message || this.dataset.buyError);
      }
      await this.trackAddToCart(form);
      window.location.assign(`${root}checkout`);
    } catch (err) {
      this.buying = false;
      buttons.forEach((b) => {
        b.disabled = false;
        b.removeAttribute('aria-busy');
      });
      if (errorEl) {
        errorEl.querySelector('[data-tv-buy-error-text]')?.remove();
        const span = document.createElement('span');
        span.setAttribute('data-tv-buy-error-text', '');
        span.textContent = ` ${err.message || this.dataset.buyError}`;
        errorEl.appendChild(span);
        errorEl.classList.remove('hidden');
      }
    }
  }

  /* ---------- Sticky bar ---------- */
  bindSticky() {
    const bar = this.querySelector('[data-tv-sticky]');
    const anchor = this.querySelector('[data-tv-buy-button]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    bar.hidden = false;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const below = entry.boundingClientRect.top > 0;
          // Show only after the main button has scrolled up out of view.
          bar.classList.toggle('is-visible', !entry.isIntersecting && !below);
        });
      },
      { threshold: 0 }
    );
    io.observe(anchor);
  }
}

if (!customElements.get('tv-pdp')) customElements.define('tv-pdp', TvPdp);
